import { useState } from "react";

import _ from "lodash";
import { api } from "../shared/utilities/api";
import apiConfigs from "../shared/utilities/apiConfigs";

import CircularProgress from "@material-ui/core/CircularProgress";

const PatientLoginForm = ({ orgId, handleLoginSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [values, setValues] = useState({
        hin: "",
        dob: "",
    });

    const [error, setError] = useState({
        hin: false,
        dob: false,
    });

    const handleChange = (e) => {
        const { id, value } = e.target;

        if (id === "hin") {
            if (/^\d*$/.test(value) && value.length <= 10) {
                setValues((prevValues) => ({
                    ...prevValues,
                    hin: value,
                }));
            }
        } else if (id === "dob") {
            let formattedValue = value.replace(/[^0-9]/g, "");

            if (formattedValue.length > 4) {
                formattedValue =
                    formattedValue.slice(0, 4) + "-" + formattedValue.slice(4);
            }
            if (formattedValue.length > 7) {
                formattedValue =
                    formattedValue.slice(0, 7) +
                    "-" +
                    formattedValue.slice(7, 9);
            }
            setValues((prevValues) => ({
                ...prevValues,
                dob: formattedValue,
            }));
        }
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);

            const result = await api(
                apiConfigs.baseOption(
                    "get",
                    `orgs/${orgId}/patients/${values.hin}`
                )
            );

            if (result.status !== 200) {
                throw new Error("Unable to retrieve patient");
            }

            const resultDOB = _.get(result, "data.formattedDOB", "");
            const resultHin = _.get(result, "data.hin", "");

            if (resultHin === values.hin && resultDOB === values.dob) {
                handleLoginSuccess(result.data);
            } else {
                alert("LOGIN FAILED");
            }
        } catch (error) {
            console.error("Error patient login:", error);
        } finally {
            setLoading(false);
        }
    };

    const disableButton = () =>
        error.hin || error.dob || !values.hin || !values.dob || loading;

    // ── Shared tokens ──────────────────────────────────────────
    const ORANGE = "#EA8331";
    const ORANGE_DARK = "#C96820";
    const WARM_BG = "#FEF6EE";
    const WARM_BORDER = "#F0D8BE";
    const WARM_LABEL = "#7A4A25";
    const WARM_HINT = "#B08060";
    const WARM_TEXT = "#3D2009";

    const inputStyle = (hasError) => ({
        width: "100%",
        padding: "12px 15px",
        borderRadius: 12,
        border: `1px solid ${hasError ? "#D85A30" : WARM_BORDER}`,
        background: "#FFFFFF",
        fontSize: 15,
        color: WARM_TEXT,
        fontFamily: "Roboto, sans-serif",
        outline: "none",
        transition: "border-color 0.15s",
        boxSizing: "border-box",
    });

    return (
        <div
            style={{
                minHeight: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "32px 16px",
                background: "#FDF0E4",
            }}
        >
            <div style={{ width: "100%", maxWidth: 520 }}>

                {/* ── Warm card wrapper ── */}
                <div
                    style={{
                        background: WARM_BG,
                        borderRadius: 20,
                        border: `1px solid ${WARM_BORDER}`,
                        padding: 28,
                    }}
                >

                    {/* ── Orange header banner ── */}
                    <div
                        style={{
                            background: ORANGE,
                            borderRadius: 14,
                            padding: "20px 24px",
                            display: "flex",
                            alignItems: "center",
                            gap: 14,
                            marginBottom: 28,
                        }}
                    >
                        {/* Icon box */}
                        <div
                            style={{
                                width: 48,
                                height: 48,
                                background: "rgba(255,255,255,0.2)",
                                borderRadius: 12,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                            }}
                        >
                            {/* Clipboard-heart SVG icon */}
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#FFFFFF"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
                                <rect x="9" y="3" width="6" height="4" rx="2" />
                                <path d="M12 12.5a2 2 0 0 1 4 0c0 1.5-2 3-4 3s-4-1.5-4-3a2 2 0 0 1 4 0z" />
                            </svg>
                        </div>

                        <div>
                            <h2
                                style={{
                                    margin: 0,
                                    color: "#FFFFFF",
                                    fontSize: 20,
                                    fontWeight: 500,
                                    fontFamily: "Roboto, sans-serif",
                                    lineHeight: "26px",
                                }}
                            >
                                Patient Login
                            </h2>
                            <p
                                style={{
                                    margin: "3px 0 0",
                                    color: "rgba(255,255,255,0.72)",
                                    fontSize: 13,
                                    fontFamily: "Roboto, sans-serif",
                                }}
                            >
                                Access your personal health report
                            </p>
                        </div>
                    </div>

                    {/* ── HIN field ── */}
                    <div style={{ marginBottom: 20 }}>
                        <label
                            htmlFor="hin"
                            style={{
                                display: "block",
                                fontSize: 13,
                                fontWeight: 500,
                                color: WARM_LABEL,
                                marginBottom: 7,
                                fontFamily: "Roboto, sans-serif",
                            }}
                        >
                            Health Insurance Number
                        </label>
                        <input
                            id="hin"
                            value={values.hin}
                            onChange={handleChange}
                            onBlur={() =>
                                setError({
                                    ...error,
                                    hin: !/^\d{10}$/.test(values.hin),
                                })
                            }
                            placeholder="1234567890"
                            style={inputStyle(error.hin)}
                            onFocus={(e) =>
                                (e.target.style.borderColor = error.hin
                                    ? "#D85A30"
                                    : ORANGE)
                            }
                        />
                        <p
                            style={{
                                margin: "6px 0 0",
                                fontSize: 12,
                                color: error.hin ? "#D85A30" : WARM_HINT,
                                fontFamily: "Roboto, sans-serif",
                            }}
                        >
                            {error.hin
                                ? "Enter a valid 10-digit number."
                                : "10-digit number · found on your health card"}
                        </p>
                    </div>

                    {/* ── DOB field ── */}
                    <div style={{ marginBottom: 24 }}>
                        <label
                            htmlFor="dob"
                            style={{
                                display: "block",
                                fontSize: 13,
                                fontWeight: 500,
                                color: WARM_LABEL,
                                marginBottom: 7,
                                fontFamily: "Roboto, sans-serif",
                            }}
                        >
                            Date of Birth
                        </label>
                        <input
                            id="dob"
                            value={values.dob}
                            onChange={handleChange}
                            onBlur={() =>
                                setError({
                                    ...error,
                                    dob: !/^\d{4}-\d{2}-\d{2}$/.test(
                                        values.dob
                                    ),
                                })
                            }
                            placeholder="YYYY-MM-DD"
                            style={inputStyle(error.dob)}
                            onFocus={(e) =>
                                (e.target.style.borderColor = error.dob
                                    ? "#D85A30"
                                    : ORANGE)
                            }
                        />
                        <p
                            style={{
                                margin: "6px 0 0",
                                fontSize: 12,
                                color: error.dob ? "#D85A30" : WARM_HINT,
                                fontFamily: "Roboto, sans-serif",
                            }}
                        >
                            {error.dob
                                ? "Use format YYYY-MM-DD."
                                : "Example: 1994-08-23"}
                        </p>
                    </div>

                    {/* ── CTA button ── */}
                    <button
                        disabled={disableButton()}
                        onClick={handleSubmit}
                        style={{
                            width: "100%",
                            padding: "14px 0",
                            borderRadius: 50,
                            border: "none",
                            background: disableButton() ? "#E5C9B0" : ORANGE,
                            color: disableButton()
                                ? "rgba(255,255,255,0.6)"
                                : "#FFFFFF",
                            fontSize: 15,
                            fontWeight: 500,
                            fontFamily: "Roboto, sans-serif",
                            cursor: disableButton() ? "not-allowed" : "pointer",
                            transition: "background 0.15s, transform 0.1s",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 10,
                            boxShadow: disableButton()
                                ? "none"
                                : "0 8px 24px rgba(234,131,49,0.32)",
                        }}
                        onMouseEnter={(e) => {
                            if (!disableButton())
                                e.currentTarget.style.background = ORANGE_DARK;
                        }}
                        onMouseLeave={(e) => {
                            if (!disableButton())
                                e.currentTarget.style.background = ORANGE;
                        }}
                    >
                        {loading ? (
                            <>
                                <CircularProgress
                                    size={18}
                                    style={{ color: "#FFFFFF" }}
                                />
                                Logging in…
                            </>
                        ) : (
                            "View My Report"
                        )}
                    </button>

                    {/* ── Trust badges ── */}
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 22,
                            marginTop: 18,
                            flexWrap: "wrap",
                        }}
                    >
                        {[
                            { icon: "🔒", label: "Encrypted" },
                            { icon: "🛡", label: "Private" },
                            { icon: "🚫", label: "Not shared" },
                        ].map(({ icon, label }) => (
                            <div
                                key={label}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 5,
                                    fontSize: 12,
                                    color: WARM_HINT,
                                    fontFamily: "Roboto, sans-serif",
                                }}
                            >
                                <span aria-hidden="true">{icon}</span>
                                {label}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PatientLoginForm;